import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  ArrowLeft, Edit, Calendar, User, TrendingUp, Target,
  CheckCircle, AlertTriangle, XCircle, Download,
  FileText, Users, Building, MapPin, BarChart3, Briefcase,
  IndianRupee, ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import targetService from '../../../api/crm/targets/targetService';
import { API_ENDPOINTS } from '../../../api/config';
import { capitalizeFirst } from '../../../utils/stringUtils';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import { toast } from 'react-hot-toast';

const TargetDetails = () => {
  const { targetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isManager = user?.role === 'sales_manager' || user?.role === 'export_manager';

  const currentUserId =
    user?.id ||
    user?.userId ||
    (() => {
      try {
        const persisted = JSON.parse(localStorage.getItem('user_data') || '{}');
        return persisted?.id || persisted?.userId || persisted?._id;
      } catch (e) {
        return user?._id;
      }
    })();

  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const [revenueQuotations, setRevenueQuotations] = useState([]);
  const [loadingRevenueQuotations, setLoadingRevenueQuotations] = useState(false);
  const [dealRows, setDealRows] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Sorting states
  const [oppSortColumn, setOppSortColumn] = useState('createdAt');
  const [oppSortDirection, setOppSortDirection] = useState('desc');

  const [revSortColumn, setRevSortColumn] = useState('createdAt');
  const [revSortDirection, setRevSortDirection] = useState('desc');

  const [dealSortColumn, setDealSortColumn] = useState('acceptedDate');
  const [dealSortDirection, setDealSortDirection] = useState('desc');

  const targetTypes = [
    { id: 'revenue', name: 'Revenue Target', description: 'Total sales revenue target' },
    { id: 'deals', name: 'Deal Count', description: 'Number of deals closed' },
    { id: 'newOpps', name: 'New Opportunities', description: 'New opportunities created' },
    { id: 'activities', name: 'Activities', description: 'Sales activities completed' }
  ];

  // Fetch target data
  useEffect(() => {
    const loadTarget = async () => {
      if (!targetId) {
        setError('Target ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await targetService.getTargetById(targetId);

        if (result.success && result.target) {
          setTarget(result.target);
        } else {
          setError(result.error || 'Failed to load target details');
        }
      } catch (err) {
        setError(err.message || 'Failed to load target details');
      } finally {
        setLoading(false);
      }
    };

    loadTarget();
  }, [targetId]);

  // Fetch CRM team members for assignee details
  useEffect(() => {
    const fetchCRMUsers = async () => {
      try {
        const params = new URLSearchParams();
        params.append('roleFilter', 'sales');
        if (isManager && currentUserId) {
          params.append('managerId', currentUserId);
        }

        const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${params.toString()}`;
        const response = await authFetch(url, {
        credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json();

        if (result.responseCode === 200) {
          const crmUsers = result.data?.users || [];
          const transformedUsers = crmUsers.map(user => ({
            id: user.user || user._id || user.id,
            name: user.name || 'Unknown',
            email: user.email || '',
            role: user.role || '',
            department: user.department || '',
            designation: user.designation || '',
            team: user.department || 'Sales Team'
          }));

          // Self option for manager/user self-assignment
          if (currentUserId) {
            const selfExists = transformedUsers.some(u => u.id === currentUserId);
            if (!selfExists) {
              transformedUsers.push({
                id: currentUserId,
                name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
                email: user?.email || '',
                role: user?.role || '',
                department: user?.department || '',
                designation: user?.designation || '',
                team: user?.department || 'Sales Team'
              });
            }
          }

          transformedUsers.sort((a, b) => a.name.localeCompare(b.name));
          setTeamMembers(transformedUsers);
        }
      } catch (error) {
      }
    };

    fetchCRMUsers();
  }, [isManager, currentUserId, user?.firstName, user?.lastName, user?.name, user?.email, user?.role, user?.department]);

  // Fetch opportunities for "New Opportunities" targets
  useEffect(() => {
    const loadOpportunities = async () => {
      if (!targetId || !target || target.type !== 'newOpps') {
        return;
      }

      try {
        setLoadingOpportunities(true);
        const result = await targetService.getTargetOpportunities(targetId);

        if (result.success) {
          setOpportunities(result.opportunities || []);
        } else {
        }
      } catch (err) {
      } finally {
        setLoadingOpportunities(false);
      }
    };

    loadOpportunities();
  }, [targetId, target]);

  // Fetch revenue quotations for "Revenue" targets
  useEffect(() => {
    const loadRevenueQuotations = async () => {
      if (!targetId || !target || target.type !== 'revenue') {
        return;
      }

      try {
        setLoadingRevenueQuotations(true);
        const result = await targetService.getTargetRevenueQuotations(targetId);

        if (result.success) {
          setRevenueQuotations(result.quotations || []);
        } else {
        }
      } catch (err) {
      } finally {
        setLoadingRevenueQuotations(false);
      }
    };

    loadRevenueQuotations();
  }, [targetId, target]);

  // Fetch deals (opportunities + accepted quotations) for "Deal Count" targets
  useEffect(() => {
    const loadDeals = async () => {
      if (!targetId || !target || target.type !== 'deals') {
        return;
      }

      try {
        setLoadingDeals(true);
        const result = await targetService.getTargetDeals(targetId);

        if (result.success) {
          setDealRows(result.deals || []);
        } else {
        }
      } catch (err) {
      } finally {
        setLoadingDeals(false);
      }
    };

    loadDeals();
  }, [targetId, target]);

  const handleExport = async () => {
    if (isExporting || !targetId) return;
    setIsExporting(true);
    const toastId = toast.loading('Preparing Excel export...');
    try {
      const exportName = target?.targetId || target?.name || target?.type || 'Target';
      const result = await targetService.exportTargetDetail(targetId, exportName);
      if (result.success) {
        toast.success('Target exported successfully', { id: toastId });
      } else {
        toast.error(result.error || 'Failed to export target', { id: toastId });
      }
    } catch (err) {
      toast.error(err.message || 'Failed to export target', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${formatIndianAmount(amount)}`;
  };

  // Sorting handlers
  const handleOppSort = (column) => {
    if (oppSortColumn === column) {
      setOppSortDirection(oppSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOppSortColumn(column);
      setOppSortDirection('asc');
    }
  };

  const handleRevSort = (column) => {
    if (revSortColumn === column) {
      setRevSortDirection(revSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setRevSortColumn(column);
      setRevSortDirection('asc');
    }
  };

  const handleDealSort = (column) => {
    if (dealSortColumn === column) {
      setDealSortDirection(dealSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDealSortColumn(column);
      setDealSortDirection('asc');
    }
  };

  // Sorted data getters
  const getSortedOpportunities = () => {
    if (!oppSortColumn || !opportunities || opportunities.length === 0) return opportunities;

    return [...opportunities].sort((a, b) => {
      let aValue, bValue;

      if (oppSortColumn === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (oppSortColumn === 'owner') {
        aValue = (a.owner?.name || '').toLowerCase();
        bValue = (b.owner?.name || '').toLowerCase();
      } else if (oppSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else if (oppSortColumn === 'stage') {
        aValue = (a.stage || '').toLowerCase();
        bValue = (b.stage || '').toLowerCase();
      } else if (oppSortColumn === 'amount') {
        aValue = a.amount || 0;
        bValue = b.amount || 0;
      } else {
        aValue = a[oppSortColumn] || '';
        bValue = b[oppSortColumn] || '';
      }

      const order = oppSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  const getSortedRevenueQuotations = () => {
    if (!revSortColumn || !revenueQuotations || revenueQuotations.length === 0) return revenueQuotations;

    return [...revenueQuotations].sort((a, b) => {
      let aValue, bValue;

      if (revSortColumn === 'quoteNumber') {
        aValue = (a.quoteNumber || '').toLowerCase();
        bValue = (b.quoteNumber || '').toLowerCase();
      } else if (revSortColumn === 'customer') {
        aValue = (a.customer?.name || '').toLowerCase();
        bValue = (b.customer?.name || '').toLowerCase();
      } else if (revSortColumn === 'owner') {
        aValue = (a.owner?.name || '').toLowerCase();
        bValue = (b.owner?.name || '').toLowerCase();
      } else if (revSortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (revSortColumn === 'totalAmount') {
        aValue = a.totalAmount || 0;
        bValue = b.totalAmount || 0;
      } else if (revSortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[revSortColumn] || '';
        bValue = b[revSortColumn] || '';
      }

      const order = revSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  const getSortedDeals = () => {
    if (!dealSortColumn || !dealRows || dealRows.length === 0) return dealRows;

    return [...dealRows].sort((a, b) => {
      let aValue, bValue;

      if (dealSortColumn === 'opportunity') {
        aValue = (a.opportunity?.name || '').toLowerCase();
        bValue = (b.opportunity?.name || '').toLowerCase();
      } else if (dealSortColumn === 'quoteNumber') {
        aValue = (a.quoteNumber || '').toLowerCase();
        bValue = (b.quoteNumber || '').toLowerCase();
      } else if (dealSortColumn === 'owner') {
        aValue = (a.owner?.name || '').toLowerCase();
        bValue = (b.owner?.name || '').toLowerCase();
      } else if (dealSortColumn === 'acceptedDate') {
        aValue = new Date(a.acceptedDate || 0).getTime();
        bValue = new Date(b.acceptedDate || 0).getTime();
      } else if (dealSortColumn === 'totalAmount') {
        aValue = a.totalAmount || 0;
        bValue = b.totalAmount || 0;
      } else {
        aValue = a[dealSortColumn] || '';
        bValue = b[dealSortColumn] || '';
      }

      const order = dealSortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Get stage badge color
  const getStageColor = (stage) => {
    switch (stage) {
      case 'closed_won':
        return 'bg-green-100 text-green-800';
      case 'closed_lost':
        return 'bg-red-100 text-red-800';
      case 'qualified':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get Status Color
  // Get Status Color (uses computedStatus - system-driven, never manual)
  const getStatusColor = (status, computedStatus) => {
    // Always use computedStatus (system-driven), fallback to status only if computedStatus is missing
    const effectiveStatus = (computedStatus || status || 'draft').toLowerCase();
    switch (effectiveStatus) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'incomplete':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format status label
  // Format status label (always use computedStatus - system-driven)
  const formatStatusLabel = (status, computedStatus) => {
    const safeStatus = computedStatus || status || 'draft';
    return capitalizeFirst(safeStatus.toString());
  };

  // Get Type Badge Color
  const getTypeColor = (type) => {
    switch (type) {
      case 'revenue':
        return 'bg-blue-100 text-blue-800';
      case 'deals':
        return 'bg-purple-100 text-purple-800';
      case 'newOpps':
        return 'bg-green-100 text-green-800';
      case 'activities':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get human readable metric label
  const getMetricLabel = (type) => {
    const found = targetTypes.find(t => t.id === type);
    return found?.name || capitalizeFirst(type || 'target');
  };

  // Calculate over-achievement (extra achieved above target)
  const rawOverAchieved = (target?.achievedValue || 0) - (target?.value || 0);
  const overAchieved = Math.max(rawOverAchieved, 0);

  // Enrich target with assignee details
  const enrichedTarget = target ? (() => {
    let enriched = { ...target };

    if (target.scope === 'team' && Array.isArray(target.assignees) && target.assignees.length > 0) {
      const assigneeDetails = target.assignees
        .map(id => teamMembers.find(m => m.id === id))
        .filter(Boolean);

      if (assigneeDetails.length > 0) {
        enriched.assigneeDetails = assigneeDetails;
        enriched.assigneeNames = assigneeDetails.map(m => m.name || 'Unknown');
      }
    } else if (target.assignee) {
      const primary = teamMembers.find(m => m.id === target.assignee);
      if (primary) {
        enriched.assigneeDetails = [primary];
        enriched.assigneeName = primary.name || target.assigneeName;
      }
    }

    return enriched;
  })() : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !target) {
    return (
      <div className="mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Target Not Found</h3>
          <p className="text-gray-600 mb-4">{error || 'The target you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/created_targets')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Targets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      {/* Breadcrumb / Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-gray-900 capitalize">
              {target.name || `${target.type} - ${target.period}`}
            </h1>
            {/* Context line: clearly show metric, scope and owner/team */}
            <p className="text-sm text-gray-600">
              <span className="font-medium">{getMetricLabel(target.type)}</span>
              {' · '}
              <span className="capitalize">
                {target.scope === 'team' ? 'Team target' : 'Individual target'}
              </span>
              {' · '}
              <span>
                {target.scope === 'team'
                  ? (target.teamName || target.team || 'Unnamed team')
                  : (enrichedTarget?.assigneeDetails?.[0]?.name || target.assigneeName || 'Unassigned owner')}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center space-x-2 px-4 py-2 rounded-md transition-colors bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              title="Export Target"
            >
              <Download className="h-4 w-4" />
              <span>{isExporting ? 'Exporting...' : 'Export'}</span>
            </button>
            <PermissionGate permission="target_achievement" action="update">
              <button
                onClick={() => navigate(`/target_builder?id=${target.id}`)}
                className="flex items-center space-x-2 px-4 py-2 rounded-md transition-colors bg-blue-600 text-white hover:bg-blue-700"
                title="Edit Target"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Target</span>
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Target Header Card */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Type</label>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getTypeColor(target.type)}`}>
              {targetTypes.find(t => t.id === target.type)?.name || target.type}
            </span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Status</label>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(target.status, target.computedStatus)}`}>
              {formatStatusLabel(target.status, target.computedStatus)}
            </span>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Period</label>
            <p className="text-sm text-gray-900 capitalize">{target.period}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Scope</label>
            <p className="text-sm text-gray-900 capitalize">{target.scope}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-200 pt-6">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Period Range</label>
            <p className="text-sm text-gray-900">
              {target.startDate && target.endDate
                ? `${formatDate(target.startDate)} – ${formatDate(target.endDate)}`
                : 'N/A'}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Target Value</label>
            <p className="text-lg font-semibold text-gray-900">
              {target.type === 'revenue' || target.type === 'activities'
                ? formatCurrency(target.value)
                : target.value?.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Achievement Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Target</h3>
            <Target className="h-5 w-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {target.type === 'revenue' || target.type === 'activities'
              ? formatCurrency(target.value)
              : target.value?.toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Achieved</h3>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {target.type === 'revenue' || target.type === 'activities'
              ? formatCurrency(target.achievedValue || 0)
              : Math.round(target.achievedValue || 0).toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Remaining</h3>
            <TrendingUp className="h-5 w-5 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {target.type === 'revenue' || target.type === 'activities'
              ? formatCurrency(Math.max((target.value || 0) - (target.achievedValue || 0), 0))
              : Math.round(Math.max((target.value || 0) - (target.achievedValue || 0), 0)).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Progress</h3>
          <span className="text-sm font-bold text-gray-900">
            {target.progressPercentage || target.progress || 0}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full ${(target.progressPercentage || target.progress || 0) >= 100 ? 'bg-green-500' :
              (target.progressPercentage || target.progress || 0) >= 90 ? 'bg-blue-500' :
                (target.progressPercentage || target.progress || 0) >= 80 ? 'bg-yellow-500' :
                  'bg-red-500'
              }`}
            style={{ width: `${Math.min(target.progressPercentage || target.progress || 0, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Assignee Information */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Assignee Information
          </h3>

          <div className="space-y-4">
            {target?.scope === 'team' ? (
              // Team target
              Array.isArray(target.assigneesInfo) && target.assigneesInfo.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Team Members</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {target.assigneesInfo.length} members
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {target.assigneesInfo.map((member, index) => (
                      <div
                        key={`${member.id || member.email || index}`}
                        className="flex flex-col rounded-md border border-gray-100 bg-gray-50 px-4 py-3"
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {member.name || 'Unknown'}
                        </span>
                        {member.role && (
                          <span className="text-xs text-blue-600 mt-1 capitalize">
                            {member.role.replace(/_/g, ' ')}
                          </span>
                        )}
                        {member.email && (
                          <span className="text-xs text-gray-500 mt-1">
                            {member.email}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : Array.isArray(enrichedTarget?.assigneeDetails) && enrichedTarget.assigneeDetails.length > 0 ? (
                // Fallback for team targets without assigneesInfo
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Team Members</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {enrichedTarget.assigneeDetails.length} members
                    </span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {enrichedTarget.assigneeDetails.map((member, index) => (
                      <div
                        key={`${member.id || member.email || index}`}
                        className="flex flex-col rounded-md border border-gray-100 bg-gray-50 px-4 py-3"
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {member.name || 'Unknown'}
                        </span>
                        {member.email && (
                          <span className="text-xs text-gray-500 mt-1">
                            {member.email}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-gray-100 bg-gray-50 px-4 py-3">
                  <span className="text-sm text-gray-500">No team members assigned</span>
                </div>
              )
            ) : (
              // Individual target - show single assignee with name, role, email
              <div className="rounded-md border border-gray-100 bg-gray-50 px-4 py-3">
                <span className="text-sm font-medium text-gray-900">
                  {target?.assigneeInfo?.name || target?.assigneeName || 'N/A'}
                </span>
                {target?.assigneeInfo?.role && (
                  <span className="block text-xs text-blue-600 mt-1 capitalize">
                    {target.assigneeInfo.role.replace(/_/g, ' ')}
                  </span>
                )}
                {target?.assigneeInfo?.email && (
                  <span className="block text-xs text-gray-500 mt-1">
                    {target.assigneeInfo.email}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Achievement Details */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Achievement Details
          </h3>

          <div className="space-y-4">
            {target.expiryInfo && (
              <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-600">Expiry</span>
                <span className="text-sm font-medium text-gray-900">
                  {target.expiryInfo}
                </span>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Target</span>
                <span className="text-sm font-semibold text-gray-900">
                  {target.type === 'revenue' || target.type === 'activities'
                    ? formatCurrency(target.value)
                    : target.value?.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Achieved</span>
                <span className="text-sm font-semibold text-gray-900">
                  {target.type === 'revenue' || target.type === 'activities'
                    ? formatCurrency(target.achievedValue || 0)
                    : Math.round(target.achievedValue || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Remaining</span>
                <span className="text-sm font-semibold text-gray-900">
                  {target.type === 'revenue' || target.type === 'activities'
                    ? formatCurrency(Math.max((target.value || 0) - (target.achievedValue || 0), 0))
                    : Math.round(Math.max((target.value || 0) - (target.achievedValue || 0), 0)).toLocaleString()}
                </span>
              </div>
              {overAchieved > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Over Achieved</span>
                  <span className="text-sm font-semibold text-green-700">
                    {target.type === 'revenue' || target.type === 'activities'
                      ? formatCurrency(overAchieved)
                      : Math.round(overAchieved).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-600">Progress</span>
                <span className="text-sm font-bold text-gray-900">
                  {target.progressPercentage || target.progress || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Opportunity List (Only for New Opportunities targets) */}
      {target.type === 'newOpps' && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Briefcase className="h-5 w-5 mr-2" />
              Opportunities ({opportunities.length})
            </h3>
            {loadingOpportunities && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
          </div>

          {loadingOpportunities ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Loading opportunities...</p>
            </div>
          ) : opportunities.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">No opportunities found for this target period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleOppSort('name')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Opportunity Name</span>
                        {oppSortColumn === 'name' ? (
                          oppSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleOppSort('owner')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Representative</span>
                        {oppSortColumn === 'owner' ? (
                          oppSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleOppSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created Date</span>
                        {oppSortColumn === 'createdAt' ? (
                          oppSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleOppSort('stage')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Stage</span>
                        {oppSortColumn === 'stage' ? (
                          oppSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleOppSort('amount')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Amount</span>
                        {oppSortColumn === 'amount' ? (
                          oppSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedOpportunities().map((opp) => (
                    <tr key={opp.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={opp.name || 'Unnamed Opportunity'}>
                          {opp.name || 'Unnamed Opportunity'}
                        </div>
                        {opp.description && (
                          <div className="text-xs text-gray-500 mt-1 truncate max-w-xs" title={opp.description}>
                            {opp.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {opp.owner ? (
                          <div className="min-w-0">
                            <div className="text-sm text-gray-900 truncate max-w-xs" title={opp.owner.name}>
                              {opp.owner.name}
                            </div>
                            {opp.owner.email && (
                              <div className="text-xs text-gray-500 truncate max-w-xs" title={opp.owner.email}>
                                {opp.owner.email}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(opp.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStageColor(opp.stage)}`}>
                          {capitalizeFirst(opp.stage?.replace('_', ' ') || 'N/A')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatCurrency(opp.amount || 0)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Revenue Quotations List (Only for Revenue targets) */}
      {target.type === 'revenue' && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <IndianRupee className="h-5 w-5 mr-2 font-bold" />
              Revenue Quotations ({revenueQuotations.length})
            </h3>
            {loadingRevenueQuotations && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
          </div>

          {loadingRevenueQuotations ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Loading quotations...</p>
            </div>
          ) : revenueQuotations.length === 0 ? (
            <div className="text-center py-8">
              <IndianRupee className="h-12 w-12 text-gray-400 mx-auto mb-2 font-bold" />
              <p className="text-sm text-gray-600">No revenue quotations found for this target period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleRevSort('quoteNumber')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Quotation</span>
                        {revSortColumn === 'quoteNumber' ? (
                          revSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleRevSort('customer')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Customer</span>
                        {revSortColumn === 'customer' ? (
                          revSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleRevSort('owner')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Representative</span>
                        {revSortColumn === 'owner' ? (
                          revSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleRevSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {revSortColumn === 'status' ? (
                          revSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleRevSort('totalAmount')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Amount</span>
                        {revSortColumn === 'totalAmount' ? (
                          revSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleRevSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created Date</span>
                        {revSortColumn === 'createdAt' ? (
                          revSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedRevenueQuotations().map((q) => (
                    <tr key={q.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={q.quoteNumber || 'N/A'}>
                          {q.quoteNumber || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 truncate max-w-xs" title={q.customer?.name || 'N/A'}>
                          {q.customer?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {q.owner ? (
                          <div className="min-w-0">
                            <div className="text-sm text-gray-900 truncate max-w-xs" title={q.owner.name}>
                              {q.owner.name}
                            </div>
                            {q.owner.email && (
                              <div className="text-xs text-gray-500 truncate max-w-xs" title={q.owner.email}>
                                {q.owner.email}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700">
                          {capitalizeFirst(q.status || 'N/A')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatCurrency(q.totalAmount || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(q.createdAt)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Deals List (Only for Deal Count targets) */}
      {target.type === 'deals' && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Briefcase className="h-5 w-5 mr-2" />
              Deals (Accepted Quotations) ({dealRows.length})
            </h3>
            {loadingDeals && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
          </div>

          {loadingDeals ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Loading deals...</p>
            </div>
          ) : dealRows.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                No accepted quotations found for this target period
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleDealSort('opportunity')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Opportunity</span>
                        {dealSortColumn === 'opportunity' ? (
                          dealSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleDealSort('quoteNumber')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Quotation</span>
                        {dealSortColumn === 'quoteNumber' ? (
                          dealSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleDealSort('owner')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Representative</span>
                        {dealSortColumn === 'owner' ? (
                          dealSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleDealSort('acceptedDate')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Accepted Date</span>
                        {dealSortColumn === 'acceptedDate' ? (
                          dealSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleDealSort('totalAmount')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Amount</span>
                        {dealSortColumn === 'totalAmount' ? (
                          dealSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedDeals().map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={row.opportunity?.name || 'Unknown Opportunity'}>
                          {row.opportunity?.name || 'Unknown Opportunity'}
                        </div>
                        {row.opportunity?.opportunityId && (
                          <div className="text-xs text-gray-500 truncate max-w-xs" title={row.opportunity.opportunityId}>
                            {row.opportunity.opportunityId}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 truncate max-w-xs" title={row.quoteNumber || 'N/A'}>
                          {row.quoteNumber || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {row.owner ? (
                          <div className="min-w-0">
                            <div className="text-sm text-gray-900 truncate max-w-xs" title={row.owner.name}>
                              {row.owner.name}
                            </div>
                            {row.owner.email && (
                              <div className="text-xs text-gray-500 truncate max-w-xs" title={row.owner.email}>
                                {row.owner.email}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(row.acceptedDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatCurrency(row.totalAmount || 0)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Baseline Rules */}
      {target.baselineRules && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Baseline Rules
          </h3>
          <div className="bg-gray-50 rounded-md p-4">
            <p className="text-sm text-gray-900 whitespace-pre-wrap break-words first-letter:capitalize">
              {target.baselineRules}
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default TargetDetails;
